import React, { useEffect, useState, useCallback } from 'react';
import Button from '@mui/joy/Button';
import Checkbox from '@mui/material/Checkbox';
import TextField from '@mui/material/TextField';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import SelectCompany from '../Dropdowns/SelectCompany';
import LoadingIcon from '../../../../common/LoadingIcon';
import FormControlLabel from '@mui/material/FormControlLabel';
import SelectVendorStatus from '../Dropdowns/SelectVendorStatus';
import FilterNoneTwoToneIcon from '@mui/icons-material/FilterNoneTwoTone';
import { MuiColorInput } from 'mui-color-input';
import { darkModeStepperNav } from '../../../../utils/constants';
import { useDarkModeStore } from '../../../../store/useDarkModeStore';
import { formatComponentName } from '../../../../common/GlobalFunctions';

export type CardLabelMode = 'view' | 'modify';

interface CardLabelProps {
    form: any;
    label?: string;
    initialValue: any;
    isDisabled: boolean;
    editDisabled: boolean;
    fieldName: string;
    className?: string;
    mode?: CardLabelMode;
    item: any | undefined;
    id: string | undefined;
    hasBtnAction?: boolean;
    handleCreateItem: (item: any) => void;
    handleUpdateItem: (item: any) => void;
    handleRemoveItem: (id: string | undefined) => Promise<void>;
}

const CardLabel = ({
    id, mode = 'view', item, form, hasBtnAction = true, className, fieldName, label, initialValue, isDisabled, editDisabled,
    handleRemoveItem, handleUpdateItem, handleCreateItem,
}: CardLabelProps) => {

    const { darkMode } = useDarkModeStore();
    const [value, setValue] = useState<any>(item || undefined);
    const [modeCard, setModeCard] = useState<CardLabelMode>(mode);

    const isValueObject = initialValue && typeof initialValue === 'object';

    const hasCheckBox: boolean = isValueObject && ['isDefault', 'isCompleted', 'isReadOnly'].some(prop => prop in initialValue);
    const hasCompanyId: boolean = isValueObject && 'companyId' in initialValue;
    const hasBadgeColor: boolean = isValueObject && 'badgeColor' in initialValue;
    const hasVendorStatusId: boolean = isValueObject && 'vendorStatusId' in initialValue;

    const toggleModal = useCallback(() => {
        setModeCard(prevMode => (prevMode === 'view' ? 'modify' : 'view'));
    }, []);

    const submitEdit = useCallback(() => {
        if (id && value) {
            handleUpdateItem(value);
            setModeCard('view');
        }
    }, [id, value, handleUpdateItem]);

    const isValueInvalid = () => {
        return Object.entries(value).some(([key, val]) =>
            key !== 'id' &&
            typeof val !== 'object' &&
            ((typeof val === 'string' && val.length <= 1) ||
                (typeof val === 'number' && val < 1) ||
                val === null ||
                val === undefined
            ));
    };

    useEffect(() => {
        setValue(item || undefined);
    }, [item, modeCard]);

    const renderColorInput = (label: string, isViewMode: boolean) => {
        return isViewMode
            ? <div
                key={`${label}-isViewMode-badgeColor`}
                className={`d-flex flex-row gap-1 align-items-center fw-bold ${darkMode && 'text-white'}`}
                style={{ fontSize: 12 }}
            >
                <span className="fw-bold mb-2"
                    style={{ fontSize: 13, color: '#A4A4A4' }}>{formatComponentName(label)}:</span>
                <span className='fw-cold' style={{ color: value[label] ?? 'black' }}>
                    <FilterNoneTwoToneIcon style={{ width: 14, height: 14, color: value[label] ?? 'white' }} />
                    {value[label]}
                </span>
            </div>
            : <div className='position-relative mt-2' key={`${label}-badge-color`}>
                <div
                    className={`position-absolute fw-bold ${!darkMode && 'bg-white'}`}
                    style={{ fontSize: 12, top: -8, left: 10, zIndex: 10, padding: '0 5px', color: '#A4A4A4' }}
                >
                    Color
                </div>
                <MuiColorInput
                    className='w-100'
                    format="hex"
                    value={value[label]}
                    onChange={(newValue: string) => setValue((prev: any) => ({ ...prev, [label]: newValue }))}
                    sx={{
                        '& .MuiInputBase-root': {
                            borderColor: '#585858',
                            color: darkMode ? 'white' : 'initial',
                        },
                        '& .MuiInputBase-root:hover': {
                            borderColor: '#585858',
                        },
                    }}
                />
            </div>
    }

    const renderCheckBox = (labelKey: string, isViewMode: boolean) => {
        return isViewMode
            ? <div
                key={labelKey}
                className={`d-flex flex-row gap-1 align-items-center mt-1 ${value && !value[labelKey] && 'd-none'}`}
                style={{ color: 'green' }}
            >
                <TaskAltIcon sx={{ width: 13, height: 13 }} />
                <span key={`span-key-${labelKey}`} style={{ fontSize: 12 }}>{formatComponentName(labelKey) || labelKey}</span>
            </div >
            : <FormControlLabel
                key={labelKey}
                className={`ms-1 ${darkMode && 'text-white'}`}
                label={formatComponentName(labelKey) || labelKey}
                control={<Checkbox
                    checked={value[labelKey]}
                    onChange={(e) => setValue((prev: any) => ({ ...prev, [labelKey]: e.target.checked }))}
                />}
            />
    }

    const renderVendorStatusDropdown = (label: string, isViewMode: boolean) => {
        return <SelectVendorStatus
            key={label}
            label='Select Vendor Status'
            viewMode={isViewMode}
            vendorStatusId={value[label] || ''}
            handleVendorStatus={(selectedOption) => {
                setValue((prev: any) => ({ ...prev, vendorStatusId: selectedOption ? selectedOption.value : '' }));
            }}
        />
    }

    const renderCompanyDropdown = (label: string, isViewMode: boolean) => {
        return <SelectCompany
            key={label}
            label='Select Company'
            viewMode={isViewMode}
            value={value}
            companyId={value[label] || ''}
            handleCompany={(selectedOption) => {
                setValue((prev: any) => ({ ...prev, companyId: selectedOption ? selectedOption.value : '' }));
            }}
            setValue={setValue}
        />
    }

    const renderFieldInput = (label: string, isViewMode: boolean, isTitle: boolean) => {
        return isViewMode
            ? <span className={`${darkMode && 'text-white'}`} key={`span-${label}`} style={{ fontSize: isTitle ? 'initial' : 12 }}>
                {
                    isTitle
                        ? value[label]
                        : <span key={`span-${label}-${value}`} >
                            {formatComponentName(label)}: <strong>{value[label]}</strong>
                        </span>
                }
            </span>
            : <div className={`${!isTitle && 'mt-2'} ${darkMode && 'text-white'}`} key={label}>
                <TextField
                    key={label}
                    id={label}
                    placeholder={!isTitle ? `Enter ${formatComponentName(label) || label}` : `Enter ${formatComponentName(fieldName)}`}
                    variant="outlined"
                    label={!isTitle ? formatComponentName(label) || label : `Add ${formatComponentName(fieldName)}`}
                    fullWidth
                    autoComplete="off"
                    type={typeof value[label] === 'number' ? 'number' : 'text'}
                    value={value[label] || ''}
                    onChange={(e) => {
                        const newValue = typeof value[label] === 'number'
                            ? e.target.value === '' ? 0 : parseFloat(e.target.value)
                            : e.target.value;
                        setValue((prev: any) => ({ ...prev, [label]: newValue }));
                    }}
                    InputProps={{
                        style: {
                            height: 50,
                            paddingLeft: 7,
                            borderRadius: 5,
                            color: darkMode ? 'white' : 'black',
                            background: darkMode ? '#272727' : 'white',
                            border: `1px solid ${darkMode ? '#585858' : 'white'}`,
                        },
                    }}
                />
            </div>
    }

    const renderFields = (isAddItem?: boolean) => {
        const isViewMode = modeCard === 'view' && !isAddItem;

        return Object.keys(form).map((key) => {
            const isTitle = key === 'title' || key === 'name';
            if (key === 'id') return null;

            if (hasBadgeColor && key === 'badgeColor') return renderColorInput(key, isViewMode);

            if (hasCheckBox && ['isDefault', 'isCompleted', 'isReadOnly'].includes(key)) return renderCheckBox(key, isViewMode);

            if (hasVendorStatusId && key === 'vendorStatusId') return renderVendorStatusDropdown(key, isViewMode);

            if (hasCompanyId && key === 'companyId') return renderCompanyDropdown(key, isViewMode);

            return renderFieldInput(key, isViewMode, isTitle)
        });
    };

    const renderNewCard = () => {
        return <div
            className={`card-label card p-4 ps-3 pe-3 ${className ?? ''}`}
            style={{
                borderRadius: 5,
                marginLeft: '2%',
                marginRight: '5%',
                background: darkMode ? darkModeStepperNav : 'white',
            }}
        >
            {renderFields(true)}
            <button
                onClick={() => !isDisabled && handleCreateItem(value)}
                disabled={!!value && isValueInvalid()}
                className="btn mt-3 text-white"
                style={{ width: 150, background: 'var(--btn-theme-background)' }}
            >
                {isDisabled ? <LoadingIcon width={15} /> : 'Submit'}
            </button>
        </div>
    };

    const renderActionButtons = () => {
        return <div className='card-actions' style={{ top: 20, right: 20 }}>
            {
                modeCard === 'view' &&
                <Button variant='plain' style={{ width: 35, height: 30 }} onClick={toggleModal}>
                    <EditIcon sx={{ width: 14, height: 14 }} />
                </Button>
            }
            <Button onClick={() => handleRemoveItem(id)} variant='plain' style={{ width: 35, height: 30 }}>
                <DeleteIcon sx={{ width: 14, height: 14, color: 'red' }} />
            </Button>
        </div>
    }

    const renderLabelInModifyMode = () => {
        return <label
            className="fw-bold mb-2"
            htmlFor={label || fieldName}
            style={{ fontSize: 13, color: '#A4A4A4' }}
        >
            {label && formatComponentName(label)}
        </label>
    }

    const renderSubmitButtons = () => {
        return <div className='d-flex flex-row gap-3 mt-2'>
            <button
                className="btn mt-2 text-white"
                disabled={!!value && isValueInvalid()}
                onClick={() => !editDisabled && submitEdit()}
                style={{ width: 150, background: 'var(--btn-theme-background)' }}
            >
                {editDisabled ? <LoadingIcon width={15} /> : 'Submit'}
            </button>
            <button
                onClick={() => setModeCard('view')}
                className="btn e mt-2"
                style={{
                    width: 150,
                    color: 'var(--color-pink)',
                    border: '1px solid var(--color-pink)',
                }}
            >
                Cancel
            </button>
        </div>
    }

    const renderCardContent = () => {
        return modeCard === 'view'
            ? <>{renderFields()}</>
            : <>
                {renderFields()}
                {renderSubmitButtons()}
            </>
    }

    const renderExistingCard = () => {
        return <div
            className={`card-label card p-4 ps-3 pe-3 ${className ?? ''}`}
            style={{
                borderRadius: 5,
                marginLeft: '2%',
                marginRight: '5%',
                background: darkMode ? darkModeStepperNav : 'white',
            }}
        >
            {hasBtnAction && id && renderActionButtons()}
            {modeCard === 'modify' && renderLabelInModifyMode()}

            {renderCardContent()}
        </div>
    }

    return value && value.id === ''
        ? renderNewCard()
        : renderExistingCard()
};

export default CardLabel;
